// Phase 3 — Métiers : POS, Chantiers
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, ShoppingCart, Trash2, Printer, PackageOpen } from "lucide-react";
import { api, formatXAF, formatDate, formatDateTime } from "@/lib/api";
import { useEntity } from "@/lib/entity";
import GenericModule from "@/components/GenericModule";
import Modal from "@/components/Modal";

// ==================== POS — Caisse ====================
export function POS() {
  const { currentEntityId } = useEntity();
  const [tab, setTab] = useState("caisse");
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 border-b border-line">
        {[
          { id: "caisse", label: "Caisse" },
          { id: "items", label: "Articles / Menu" },
          { id: "sales", label: "Historique ventes" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 h-10 text-sm transition-colors border-b-2 -mb-px ${tab === t.id ? "border-citron text-citron" : "border-transparent text-obsidian-400 hover:text-obsidian-100"}`}
            data-testid={`pos-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "caisse" && <PosRegister entityId={currentEntityId} />}
      {tab === "items" && <PosItems />}
      {tab === "sales" && <PosSales />}
    </div>
  );
}

function PosRegister({ entityId }) {
  const [data, setData] = useState({ items: [], categories: [] });
  const [cat, setCat] = useState("");
  const [cart, setCart] = useState([]);
  const [payment, setPayment] = useState("Espèces");
  const [customer, setCustomer] = useState("");
  const [tableNum, setTableNum] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { load(); }, [entityId]);
  async function load() {
    try {
      const { data } = await api.get(`/pos/items/${entityId}`);
      setData(data);
      if (!cat && data.categories?.length) setCat(data.categories[0]);
    } catch (e) { setData({ items: [], categories: [] }); }
  }

  const addToCart = (item) => {
    setCart((c) => {
      const existing = c.find((x) => x.item_id === item.id);
      if (existing) return c.map((x) => x.item_id === item.id ? { ...x, qty: x.qty + 1 } : x);
      return [...c, { item_id: item.id, name: item.name, qty: 1, unit_price: item.sell_price }];
    });
  };
  const updateQty = (id, qty) => {
    if (qty <= 0) return setCart((c) => c.filter((x) => x.item_id !== id));
    setCart((c) => c.map((x) => x.item_id === id ? { ...x, qty } : x));
  };
  const total = cart.reduce((s, l) => s + l.qty * l.unit_price, 0);

  async function checkout() {
    if (cart.length === 0) return;
    setBusy(true);
    try {
      await api.post("/pos/sales", { entity_id: entityId, lines: cart, payment, customer, table_number: tableNum });
      setCart([]);
      setCustomer("");
      setTableNum("");
      alert("Vente enregistrée ✓");
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  }

  const filtered = data.items?.filter((i) => i.category === cat && i.available) || [];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Menu */}
      <div className="lg:col-span-2 space-y-3">
        <div className="flex gap-1 overflow-x-auto pb-1">
          {data.categories?.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-3 h-9 rounded-sm text-xs whitespace-nowrap transition-colors ${cat === c ? "bg-citron text-obsidian-950 font-semibold" : "bg-obsidian-900 text-obsidian-300 hover:bg-obsidian-850"}`}
            >
              {c}
            </button>
          ))}
        </div>
        {data.items?.length === 0 && (
          <div className="card p-8 text-center text-obsidian-500 text-sm">
            <PackageOpen size={32} className="mx-auto mb-3 text-obsidian-700" />
            Aucun article dans le menu. <br/>Allez dans l'onglet « Articles / Menu » pour en ajouter.
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {filtered.map((item) => (
            <button
              key={item.id}
              onClick={() => addToCart(item)}
              className="card p-4 hover:bg-obsidian-850 hover:border-citron/40 text-left transition-all"
            >
              <div className="text-sm font-medium truncate">{item.name}</div>
              <div className="text-citron font-mono font-semibold mt-2">{formatXAF(item.sell_price)}</div>
              {item.stock !== undefined && <div className="text-[10px] text-obsidian-500 mt-1">Stock: {item.stock}</div>}
            </button>
          ))}
        </div>
      </div>

      {/* Cart */}
      <div className="card flex flex-col h-fit sticky top-20">
        <div className="px-5 py-3 border-b border-line flex items-center gap-2">
          <ShoppingCart size={14} className="text-citron" />
          <span className="font-heading font-bold text-sm">Panier</span>
          <span className="ml-auto pill bg-obsidian-800">{cart.length} ligne(s)</span>
        </div>
        <div className="flex-1 max-h-80 overflow-y-auto">
          {cart.length === 0 && <div className="p-6 text-center text-obsidian-500 text-sm">Panier vide</div>}
          {cart.map((line) => (
            <div key={line.item_id} className="flex items-center gap-2 px-4 py-2 border-b border-line/50">
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{line.name}</div>
                <div className="text-[10px] text-obsidian-500 font-mono">{formatXAF(line.unit_price)}</div>
              </div>
              <button onClick={() => updateQty(line.item_id, line.qty - 1)} className="btn-ghost h-6 w-6 p-0">−</button>
              <span className="font-mono text-sm w-6 text-center">{line.qty}</span>
              <button onClick={() => updateQty(line.item_id, line.qty + 1)} className="btn-ghost h-6 w-6 p-0">+</button>
              <span className="text-sm font-mono font-semibold w-20 text-right">{formatXAF(line.qty * line.unit_price)}</span>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-line space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Client" className="input h-9 text-xs" value={customer} onChange={(e) => setCustomer(e.target.value)} />
            <input placeholder="Table" className="input h-9 text-xs" value={tableNum} onChange={(e) => setTableNum(e.target.value)} />
          </div>
          <select className="input h-9 text-xs" value={payment} onChange={(e) => setPayment(e.target.value)}>
            {["Espèces", "Mobile Money", "Carte", "Crédit"].map(p => <option key={p}>{p}</option>)}
          </select>
          <div className="flex items-center justify-between pt-2">
            <span className="overline">Total</span>
            <span className="font-mono text-2xl font-bold text-citron">{formatXAF(total)}</span>
          </div>
          <button onClick={checkout} disabled={busy || cart.length === 0} className="btn-primary w-full" data-testid="pos-checkout-button">
            <Printer size={14} /> {busy ? "…" : "Encaisser"}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function PosItems() {
  return (
    <GenericModule
      title=""
      subtitle="Articles disponibles à la caisse"
      dataTestId="pos-items"
      endpoint="/pos/items"
      itemEndpoint="/pos/items"
      newButtonLabel="Nouvel article"
      extractItems={(d) => d.items || []}
      columns={[
        { key: "category", label: "Catégorie" },
        { key: "name", label: "Article" },
        { key: "buy_price", label: "PA", type: "money", align: "right" },
        { key: "sell_price", label: "PV", type: "money", align: "right" },
        { key: "stock", label: "Stock", type: "number", align: "right" },
        {
          key: "available", label: "Disponible", type: "pill",
          pillClass: (r) => r.available ? "bg-ok/15 text-ok" : "bg-obsidian-800 text-obsidian-500",
          render: (r) => <span className={`pill ${r.available ? "bg-ok/15 text-ok" : "bg-obsidian-800 text-obsidian-500"}`}>{r.available ? "Oui" : "Non"}</span>,
        },
      ]}
      fields={[
        { key: "name", label: "Nom", required: true, group: 1 },
        { key: "category", label: "Catégorie", required: true, placeholder: "Pizzas, Cocktails…", group: 1 },
        { key: "buy_price", label: "Prix achat", type: "number", group: 2 },
        { key: "sell_price", label: "Prix vente", type: "number", required: true, group: 2 },
        { key: "stock", label: "Stock initial", type: "number", group: 2 },
        { key: "available", label: "Disponible à la vente", type: "checkbox" },
      ]}
      defaults={{ name: "", category: "", buy_price: 0, sell_price: 0, stock: 0, available: true }}
    />
  );
}

function PosSales() {
  return (
    <GenericModule
      title=""
      subtitle="Tickets émis"
      dataTestId="pos-sales"
      endpoint="/pos/sales"
      newButtonLabel="(via Caisse)"
      extractStats={(d) => [
        { label: "Aujourd'hui", value: d.today_total || 0, money: true, accent: "citron", highlight: true },
        { label: "Total cumulé", value: d.total || 0, money: true },
        { label: "Tickets", value: d.count || 0, count: true },
      ]}
      columns={[
        { key: "ticket_number", label: "Ticket", type: "code" },
        { key: "at", label: "Date", type: "datetime" },
        { key: "customer", label: "Client" },
        { key: "table_number", label: "Table" },
        { key: "payment", label: "Paiement", type: "pill" },
        { key: "total", label: "Total", type: "money", align: "right" },
      ]}
      fields={[]}
      defaults={{}}
      customForm={({ close }) => <div className="text-center text-obsidian-400 text-sm p-4">Utilisez l'onglet <strong>Caisse</strong> pour créer une vente.<br/><button onClick={close} className="btn-secondary mt-4">Fermer</button></div>}
    />
  );
}

// ==================== CHANTIERS ====================
export function Chantiers() {
  return (
    <GenericModule
      title="Chantiers"
      subtitle="Projets en cours"
      dataTestId="chantiers"
      endpoint="/chantiers"
      newButtonLabel="Nouveau chantier"
      extractStats={(d) => [
        { label: "Chantiers", value: (d.items || []).length, count: true },
        { label: "Actifs", value: d.active_count || 0, count: true, accent: "citron", highlight: true },
      ]}
      columns={[
        { key: "code", label: "Code", type: "code" },
        { key: "name", label: "Nom" },
        { key: "client", label: "Client" },
        { key: "manager", label: "Resp." },
        { key: "budget", label: "Budget", type: "money", align: "right" },
        { key: "total_expenses", label: "Dépenses", type: "money", align: "right" },
        {
          key: "consumption_pct", label: "% Consommé", align: "right",
          render: (r) => (
            <span className={`font-mono text-xs ${r.consumption_pct > 90 ? "text-bad" : r.consumption_pct > 70 ? "text-warn" : "text-ok"}`}>
              {r.consumption_pct}%
            </span>
          ),
        },
        {
          key: "status", label: "Statut", type: "pill",
          pillClass: (r) => ({ planifie: "bg-obsidian-800", en_cours: "bg-citron/15 text-citron", suspendu: "bg-warn/15 text-warn", termine: "bg-ok/15 text-ok" })[r.status],
        },
      ]}
      fields={[
        { key: "name", label: "Nom du chantier", required: true, group: 1 },
        { key: "client", label: "Client", group: 1 },
        { key: "site_address", label: "Adresse du site" },
        { key: "budget", label: "Budget (XAF)", type: "number", group: 2 },
        { key: "manager", label: "Responsable", group: 2 },
        { key: "start_date", label: "Démarrage", type: "date", required: true, group: 3 },
        { key: "end_date", label: "Fin prévue", type: "date", group: 3 },
        { key: "status", label: "Statut", type: "select", options: [{ value: "planifie", label: "Planifié" }, { value: "en_cours", label: "En cours" }, { value: "suspendu", label: "Suspendu" }, { value: "termine", label: "Terminé" }], group: 3 },
        { key: "notes", label: "Notes", type: "textarea" },
      ]}
      defaults={{ name: "", client: "", site_address: "", budget: 0, manager: "", start_date: new Date().toISOString().slice(0, 10), end_date: "", status: "planifie", notes: "" }}
    />
  );
}
